import '@assets/styles/tiptap-preview.css';

import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import { articleApiClient } from '@src/api/company/article/articleApiClient';
import { Article } from '@src/api/types/article.ts';
import { useCategory } from '@src/context/CategoryContext';
import { ArticleStatus } from '@src/enum/articleStatus.ts';
import CustomError from '@src/exception/CustomError.ts';
import { useThrowAsyncError } from '@src/exception/useThrowAsyncError.ts';
import ArticleContent from '@src/features/shared/ArticleContent';
import { useScrollMobile } from '@src/hooks/useScrollMobile';
import { COMPANY_URL, ShowProps } from '@src/routes/company';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { generatePath, useParams } from 'react-router-dom';

function ArticleShow({ setFeature, setDescription }: ShowProps) {
  const { id } = useParams<{ id: string }>();
  const throwAsyncError = useThrowAsyncError();
  const [article, setArticle] = useState<Article | undefined>(undefined);
  const [displayRelatedArticles, setDisplayRelatedArticles] = useState<Article[]>([]);
  const { menuMediaCategories } = useCategory();

  useEffect(() => {
    if (!id) {
      return;
    }

    window.scrollTo(0, 0);

    const fetchData = async () => {
      const res = await articleApiClient.show(Number(id));
      handleApiResponse(res, async (data) => {
        if (`${data.status}` !== ArticleStatus.PUBLISHED.toString()) {
          throwAsyncError(new CustomError('記事未公開', 404));
        }
        setArticle(data);
        setDisplayRelatedArticles(data.related_articles as Article[]);
        setFeature(data.title);
        setDescription?.(data.description);
      });
    };

    fetchData();
  }, [id, throwAsyncError]);

  const { renderScrollMobile } = useScrollMobile({
    items: menuMediaCategories,
  });

  const shareData = article
    ? {
        url: `${window.location.origin}${generatePath(COMPANY_URL.ARTICLES_SHOW, { id: article.id })}`,
        title: article.title,
        tags: article.tags ? article.tags.map((tag) => tag.name) : [],
      }
    : null;

  return (
    <main className='pb-16 pt-10 md:pt-4'>
      <Helmet>
        <meta
          name='description'
          content={article?.description}
        />
        <meta
          property='og:title'
          content={article?.title}
        />
        <meta
          property='og:description'
          content={article?.description}
        />
        {article?.thumbnail_url && (
          <meta
            property='og:image'
            content={article.thumbnail_url}
          />
        )}
      </Helmet>
      <div className='m-auto max-w-[740px] lg:px-6'>
        <div className='hidden lg:-order-1 lg:block'>
          <div className='ml-[-24px] w-[calc(100%+48px)] pl-[24px]'>{renderScrollMobile()}</div>
        </div>
        {article && (
          <ArticleContent
            article={article}
            relatedArticles={displayRelatedArticles}
            shareData={shareData}
          />
        )}
      </div>
    </main>
  );
}

export default ArticleShow;
